import { useState } from "react";
import { Card, Nav } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import { NotifChanges } from "./notifchanges";
import { IconMoney } from "../common/icons";
import { Link } from "react-router";
import { notificationIcons } from "./common";
import { Button } from "../common/Button.tsx";

export function NotifCard({ notif, idx, relationType, duration, resolvedLanguage }) {
  const { t } = useTranslation();

  const [showChanges, setShowChanges] = useState(false);
  const toggleShowChanges = () => setShowChanges((current) => !current);

  const isEditNotif = notif.subType.includes("EDIT");

  const created = notif.updateInfoJSON?.createdDateTime ?? notif.timestamp;
  const createdDate = new Date(created);

  // hack to correct Java LocalDate with server using UTC timezone
  if (import.meta.env.PROD) {
    createdDate.setUTCMinutes(createdDate.getUTCMinutes() - createdDate.getTimezoneOffset());
  }

  const isModel = Number(notif.metaData?.personNr) === 0;

  const titles = {
    PERSON: () => (isEditNotif ? t("PersonEdited", "Muokattu henkilö") : t("PersonCreated", "Uusi henkilö")),
    CONTRACT: () =>
      isEditNotif ? t("ContractEdited", "Muokattu työsopimus") : t("ContractCreated", "Uusi työsopimus"),
    COMPETENCE_COURSE: () =>
      isEditNotif ? t("CourseEdited", "Muokattu muu koulutus") : t("CourseCreated", "Uusi muu koulutus"),
    COMPETENCE_EDUCATION: () =>
      isEditNotif ? t("EducationEdited", "Muokattu koulutus") : t("EducationCreated", "Uusi koulutus"),
    COMPETENCE_SKILL: () => (isEditNotif ? t("SkillEdited", "Muokattu taito") : t("SkillCreated", "Uusi taito")),
    COMPETENCE_LANGUAGE: () =>
      isEditNotif ? t("LanguageEdited", "Muokattu kielitaito") : t("LanguageCreated", "Uusi kielitaito"),
    DISCUSSION: () =>
      isEditNotif ? t("DiscussionEdited", "Muokattu keskustelu") : t("DiscussionCreated", "Uusi keskustelu"),
    EQUIPMENT: () =>
      isEditNotif ? t("EquipmentEdited", "Muokattu välineistö") : t("EquipmentCreated", "Uusi välineistö"),
  };

  const modelTitles = {
    CONTRACT: () =>
      isEditNotif
        ? t("ContractModelEdited", "Muokattu työsopimusmalli")
        : t("ContractModelCreated", "Uusi työsopimusmalli"),
    DISCUSSION: () =>
      isEditNotif
        ? t("DiscussionModelEdited", "Muokattu keskustelumalli")
        : t("DiscussionModelCreated", "Uusi keskustelumalli"),
  };

  const relationTypeTranslations = {
    PERSON: () => t("ofperson", "henkilön"),
    CONTRACT: () => t("ofcontract", "työsopimuksen"),
    COMPETENCE_COURSE: () => t("ofcourse", "muun koulutuksen"),
    COMPETENCE_EDUCATION: () => t("ofeducation", "tutkinnon"),
    COMPETENCE_SKILL: () => t("ofskill", "taidon"),
    COMPETENCE_LANGUAGE: () => t("oflanguageskill", "kielitaidon"),
    DISCUSSION: () => t("ofdiscussion", "keskustelun"),
    EQUIPMENT: () => t("ofequipment", "välineistön"),
  };

  const relationTypeModelTranslations = {
    CONTRACT: () => t("ofcontractmodel", "työsopimusmallin"),
    DISCUSSION: () => t("ofdiscussionmodel", "keskustelumallin"),
  };

  const relationTypeTranslation = relationTypeTranslations[notif.relationType];
  const relationTypeModelTranslation = relationTypeModelTranslations[notif.relationType];

  const notificationIcon =
    notificationIcons[relationType] === undefined ? (
      <IconMoney bgColor="#8FE3D0" fgColor="#477168" />
    ) : (
      notificationIcons[relationType]()
    );

  const title =
    titles[relationType] === undefined
      ? notif.subType === "PAYMENTS_APPROVAL"
        ? t("PaymentsApproval", "Maksujen hyväksyntä")
        : null
      : titles[relationType]();

  return (
    <Card data-id={notif.notificationId} data-idx={idx}>
      {!notif.readByCurrentUser && <div className="unread" />}
      <Card.Body className={notif.readByCurrentUser ? "notification-read" : ""}>
        <div className="notification-header">
          {notificationIcon} {isModel ? (modelTitles[relationType] ? modelTitles[relationType]() : title) : title}
          <time className="float-end" dateTime={created} title={createdDate.toLocaleString(resolvedLanguage)}>
            {duration(createdDate)}
          </time>
        </div>
        <Card.Text>{notif.payload}</Card.Text>
        <Card.Text className="notification-content">
          {notif.subType === "PAYMENTS_APPROVAL" ? (
            <Nav.Link as={Link} to="/epalkat/paymentapproval">
              Uusi maksu hyväksyttävänä
            </Nav.Link>
          ) : (
            <>
              {notif.metaData?.doneBy ?? notif.sender} {isEditNotif ? t("edited", "muokkasi") : t("added", "lisäsi")}{" "}
              {isModel
                ? relationTypeModelTranslation === undefined
                  ? "?"
                  : `${relationTypeModelTranslation()} ${notif.metaData.contractIdentifier}`
                : relationTypeTranslation === undefined
                  ? "?"
                  : relationTypeTranslation()}{" "}
              {/* notif.metaData.title? */}
              {isEditNotif ? t("data", "tietoja") : ""}
              {!isModel && notif.metaData.personNr && (
                <>
                  {relationType === "PERSON" ? " " : ` ${t("toperson", "henkilölle")} `}
                  <em>{`${notif.metaData.personNr} - ${notif.metaData.personName}`}</em>
                </>
              )}
            </>
          )}
        </Card.Text>
        {isEditNotif && (
          <>
            <Button variant="link" className="p-0 fs-75" onClick={toggleShowChanges}>
              {showChanges ? t("HideChanges", "Piilota muutokset") : t("Changes", "Muutokset")}
            </Button>
            {showChanges && <NotifChanges json={notif.metaData.changes} />}
          </>
        )}
      </Card.Body>
    </Card>
  );
}
